import axios from 'axios';
import store from '@/store';
import { Toast } from 'vant';
import { encrypt, decrypt } from './aes';

const service = axios.create({
  // baseURL: 'http://106.52.44.147:3000/api', // 直接指向后端服务地址和路由前缀
  // baseURL: 'http://localhost:3000/api', // 直接指向后端服务地址和路由前缀
  // baseURL: 'https://biglemon.site/api',
  baseURL: '/api', // 同域 Nginx 反代到后端（mixue.biglemon.site）
  timeout: 20000,
});

// 不需要加密的接口白名单
const NO_ENCRYPT_URLS = [
  '/appConfig/list',
  '/search-location',
];

// 判断是否需要加密
function needEncrypt(url) {
  return !NO_ENCRYPT_URLS.some(item => url.includes(item));
}

// 支持自动复扫刷新的渠道标识
const AUTO_REFRESH_CHANNEL = 'A16-L';

function isTokenExpiredError(message) {
  if (!message) return false;
  return message.includes('Access-Token失效') ||
    (message.includes('Access-Token') && (message.includes('失效') || message.includes('过期')));
}

function canAutoRefreshToken() {
  const { refreshChannel, wxid } = store.state;
  return refreshChannel === AUTO_REFRESH_CHANNEL && !!wxid;
}

let refreshingPromise = null;

async function refreshMxAccessToken() {
  if (!canAutoRefreshToken()) {
    throw new Error('Access-Token失效，请重新输入卡密');
  }

  if (!refreshingPromise) {
    const loadingToast = Toast.loading({
      message: '正在获取最新 Token...',
      forbidClick: true,
      duration: 0,
    });

    refreshingPromise = (async () => {
      try {
        const wxid = store.state.wxid;

        const response = await service.post('/a16/get-mx-access-token', {
          wxid,
          teaType: store.state.teaType || 24,
          uuid: store.state.uuid,
        });

        const newToken = response.data?.accessToken;
        if (!newToken) {
          throw new Error('刷新 Access-Token 失败');
        }

        store.commit('SET_MX_ACCESS_TOKEN', newToken);
        console.log(`[Token刷新] 渠道 ${AUTO_REFRESH_CHANNEL} 已通过 wxid 复扫获取新 Access-Token`);
        return newToken;
      } finally {
        loadingToast.clear();
      }
    })().finally(() => {
      refreshingPromise = null;
    });
  }

  return refreshingPromise;
}

async function retryMxRequestWithNewToken(config) {
  const newToken = await refreshMxAccessToken();

  const originalData = { ...(config._originalData || {}) };
  if (Object.prototype.hasOwnProperty.call(originalData, 'accessToken')) {
    originalData.accessToken = newToken;
  }

  config._tokenRetried = true;
  config.data = originalData;
  return service(config);
}

service.interceptors.request.use(
  config => {
    // 打印请求信息
    console.log('[请求信息] 接口:', config.url);
    console.log('[请求信息] 方法:', config.method);
    
    // 在 POST 请求体中添加 mxAccessToken 并加密
    if (config.method === 'post' && config.data) {
      try {
        config._originalData = { ...config.data };
        let dataToEncrypt = { ...config.data };
        
        // 打印原始请求数据
        console.log('[请求原始数据]:', JSON.stringify(dataToEncrypt, null, 2));
        
        // 白名单接口不添加额外参数，直接使用原始数据
        if (!needEncrypt(config.url)) {
          console.log('[请求未加密] 接口:', config.url, '数据:', config.data);
          return config;
        }
        
        // 添加 mxAccessToken
        const mxAccessToken = store.state.mxAccessToken;
        if (mxAccessToken) {
          dataToEncrypt.mxAccessToken = mxAccessToken;
        }
        
        // 如果是包含 /mx 的接口，添加 teaType 参数
        console.log('[请求拦截器] URL:', config.url, '是否包含/mx:', config.url && config.url.includes('/mx'));
        if (config.url && config.url.includes('/mx')) {
          const teaType = store.state.teaType;
          console.log('[请求拦截器] teaType:', teaType);
          if (teaType) {
            dataToEncrypt.teaType = teaType;
            console.log('[请求拦截器] 已添加 teaType 到 dataToEncrypt:', dataToEncrypt);
          } else {
            console.warn('[请求拦截器] teaType 为空，未添加');
          }
        }
        
        // 加密请求数据
        console.log('[请求拦截器] 最终 dataToEncrypt:', JSON.stringify(dataToEncrypt, null, 2));
        
        // 加密整个请求体
        const encryptedData = encrypt(dataToEncrypt);
        
        // 将加密后的数据放在 encryptedData 字段中
        config.data = {
          encryptedData: encryptedData
        };
        
        console.log('[请求加密] 接口:', config.url, '已加密');
        console.log('[请求加密后数据]:', config.data);
      } catch (error) {
        console.error('[请求加密失败]:', error);
        Toast.fail('请求加密失败');
        return Promise.reject(error);
      }
    }

    return config;
  },
  error => {
    console.log(error);
    return Promise.reject(error);
  }
);

service.interceptors.response.use(
  async response => {
    // 对微信代理接口的响应直接透传
    if (response.config.url.includes('/wx-payment-proxy') || response.config.url.includes('/qrconnect')) {
      return response.data;
    }

    let res = response.data;
    console.log('[响应原始数据]:', res);

    // 解密响应数据
    try {
      // 检查响应是否有加密标记或加密数据
      if (res && res.encrypted && res.data) {
        // 判断 data 字段是字符串（需要解密）还是对象（已解密）
        if (typeof res.data === 'string') {
          // 解密 data 字段
          const decryptedData = decrypt(res.data, true);
          res.data = decryptedData;
          console.log('[响应解密] 接口:', response.config.url, '已解密');
        } else {
          // data 已经是对象，可能后端已经解密或没加密
          console.log('[响应数据] 接口:', response.config.url, 'data已是对象，无需解密');
        }
        
        // 返回 data 内部的数据（兼容后端返回格式）
        if (res.data && (res.data.status || res.data.success || res.data.code !== undefined)) {
          res = res.data;
        }
      } else if (res && typeof res === 'object' && res.encryptedData) {
        // 如果整个响应体是加密的
        const decryptedData = decrypt(res.encryptedData, true);
        res = decryptedData;
        console.log('[响应解密] 接口:', response.config.url, '已解密');
      }
    } catch (error) {
      console.error('[响应解密失败]:', error);
      // 解密失败不阻断流程，继续处理原始数据
    }

    // 对项目内其他接口保持原有逻辑
    if (res.success || res.message == 'ok' || res.status || res.code === 0) {
      return res;
      // handle error
    } else {
      const errorMessage = res.message || res.msg || 'Error';
      const config = response.config;

      if (
        config.url &&
        config.url.includes('/mx') &&
        !config._tokenRetried &&
        isTokenExpiredError(errorMessage) &&
        canAutoRefreshToken()
      ) {
        try {
          return await retryMxRequestWithNewToken(config);
        } catch (refreshError) {
          console.error('[Token刷新失败]:', refreshError);
          const error = new Error(refreshError.message || errorMessage);
          error.response = res;
          return Promise.reject(error);
        }
      }

      // 创建包含完整响应信息的错误对象
      const error = new Error(errorMessage);
      error.response = res; // 附带完整响应数据
      return Promise.reject(error);
    }
  },
  async error => {
    console.log('err' + error);
    // 处理 HTTP 错误状态码（如 410）
    if (error.response && error.response.data) {
      const res = error.response.data;
      const errorMessage = res.message || error.message || 'Error';
      const config = error.config;

      if (
        config &&
        config.url &&
        config.url.includes('/mx') &&
        !config._tokenRetried &&
        isTokenExpiredError(errorMessage) &&
        canAutoRefreshToken()
      ) {
        try {
          return await retryMxRequestWithNewToken(config);
        } catch (refreshError) {
          console.error('[Token刷新失败]:', refreshError);
          const customError = new Error(refreshError.message || errorMessage);
          customError.response = res;
          customError.status = error.response.status;
          return Promise.reject(customError);
        }
      }

      const customError = new Error(errorMessage);
      customError.response = res; // 附带完整响应数据
      customError.status = error.response.status;
      return Promise.reject(customError);
    }
    return Promise.reject(error);
  }
);

export default service; 